/**
 * useAutoLocale — o idioma do app vem do produto, não de uma escolha.
 *
 * A versão anterior tinha três prioridades, nesta ordem: escolha manual
 * gravada no dispositivo, `preferred_language` do banco, e só então o produto.
 * Na prática isso deixava 416 contas com a interface num idioma e o conteúdo
 * em outro — e a maioria nunca tinha escolhido nada: o quiz de onboarding
 * grava `preferred_language` por um clique de avanço, o que é indistinguível
 * de uma decisão.
 *
 * Agora o produto decide, porque é ele que determina em que idioma as receitas
 * estão escritas. Quem tem catálogo em mais de um idioma — cerca de 35 contas
 * — continua escolhendo, e para essas nada é imposto.
 *
 * O seletor da tela de login não passa por aqui: ele existe para a pessoa
 * conseguir ler a tela antes de entrar.
 */
import { useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import type { SupportedMarket } from "@/contexts/LocaleContext";
import type { SupportedLanguage } from "@/i18n";
import { enforcedLanguage } from "@/lib/productLanguage";

/** Mercado canônico de cada idioma, para os dois andarem juntos. */
const LANGUAGE_MARKET: Record<SupportedLanguage, SupportedMarket> = {
  en: "us",
  es: "mx",
  de: "de",
};

export const useAutoLocale = () => {
  const { user, loading } = useAuth();
  const { setLocale, language } = useLocale();

  useEffect(() => {
    if (loading || !user) return;

    // Sem produto com catálogo, ou com catálogo em mais de um idioma, não há
    // o que impor: fica o idioma atual e o seletor continua disponível.
    const imposto = enforcedLanguage(user.products);
    if (!imposto) return;

    if (imposto !== language) {
      setLocale(imposto, LANGUAGE_MARKET[imposto]);
    }
  }, [user?.id, user?.products, loading, setLocale, language]);
};
